'use client';
import React, { useRef, useEffect, useState } from 'react';

const nutrients = [
  { label: 'Protein', value: '20g', percent: 82, color: '#F9DC2A', detail: 'Per 100g of Chana Sattu' },
  { label: 'Dietary Fibre', value: '12g', percent: 68, color: '#F9DC2A', detail: 'Supports gut health' },
  { label: 'Added Sugar', value: '0g', percent: 3, color: '#F9DC2A', detail: 'In Sugar-Free Chhatua' },
  { label: 'Micronutrients', value: 'B12 + Iron + Folic Acid', percent: 90, color: '#F9DC2A', detail: 'In Chakki Atta' },
];

const labels = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Leaf / natural — No Preservatives */}
        <path d="M14 3C14 3 6 7 6 15C6 19.418 9.582 23 14 23C18.418 23 22 19.418 22 15C22 7 14 3 14 3Z" stroke="#F9DC2A" strokeWidth="1.6" strokeLinejoin="round" fill="rgba(249,220,42,0.12)"/>
        <path d="M14 23V13" stroke="#F9DC2A" strokeWidth="1.6" strokeLinecap="round"/>
        <path d="M14 17C14 17 10 14 8 11" stroke="#F9DC2A" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M14 14C14 14 17 12 19 10" stroke="#F9DC2A" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Preservative-Free',
    desc: 'No chemicals. No shortcuts. What you see on the label is what you get in the pack.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Magnifying glass with checkmark — Lab Tested */}
        <circle cx="12" cy="12" r="7" stroke="#F9DC2A" strokeWidth="1.6" fill="rgba(249,220,42,0.12)"/>
        <path d="M17.5 17.5L23 23" stroke="#F9DC2A" strokeWidth="1.8" strokeLinecap="round"/>
        <path d="M9.5 12L11.5 14L14.5 10.5" stroke="#F9DC2A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Lab Tested',
    desc: "Every batch is tested for purity and safety. If it doesn't pass, it doesn't come to you.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Sealed pouch / package — Poly-Sealed Fresh */}
        <rect x="5" y="9" width="18" height="13" rx="2.5" stroke="#F9DC2A" strokeWidth="1.6" fill="rgba(249,220,42,0.12)"/>
        <path d="M5 13H23" stroke="#F9DC2A" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M10 9V7C10 5.895 10.895 5 12 5H16C17.105 5 18 5.895 18 7V9" stroke="#F9DC2A" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M11 17H17" stroke="#F9DC2A" strokeWidth="1.4" strokeLinecap="round"/>
        <path d="M13 15V19" stroke="#F9DC2A" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    title: 'Poly-Sealed Fresh',
    desc: 'Seal that locks-in the freshness, flavour and nutrition – right till it reaches that bowl!',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Shield with checkmark — FSSAI Compliant */}
        <path d="M14 3L5 7V14C5 18.971 9.029 23 14 24C18.971 23 23 18.971 23 14V7L14 3Z" stroke="#F9DC2A" strokeWidth="1.6" strokeLinejoin="round" fill="rgba(249,220,42,0.12)"/>
        <path d="M10 14L12.5 16.5L18 11" stroke="#F9DC2A" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'FSSAI Compliant',
    desc: "Certified safe under India's food safety standards. Every pack. Every time.",
  },
];

export default function LabelTransparencySection() {
  const barsRef = useRef<HTMLDivElement>(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const el = barsRef?.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimated(true); },
      { threshold: 0.3 }
    );
    observer?.observe(el);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="label" className="py-20 md:py-32 overflow-hidden" style={{ background: '#2A1015' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="reveal">
            <span className="block text-[10px] uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--accent)' }}>
              Label Transparency
            </span>
            <h2
              className="font-display text-white leading-none"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', fontWeight: 200, letterSpacing: '0.02em' }}
            >
              Know your<br />
              <span className="italic font-light" style={{ color: 'var(--accent)' }}>Kasturi.</span>
            </h2>
          </div>
          <p className="reveal stagger-2 text-white/60 max-w-sm text-sm leading-relaxed md:text-right">
            Every pack of Kasturi contains the love, purity and sensibilities of tradition. Each grain. Each ingredient. Mindfully used. Traditionally sourced.
          </p>
        </div>

        {/* Main grid: nutrition bars + label pillars */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Nutrition bars */}
          <div ref={barsRef} className="reveal reveal-left">
            <p className="text-xs uppercase tracking-widest text-white/40 mb-8">What&apos;s actually inside</p>
            <div className="space-y-8">
              {nutrients?.map((n, i) => (
                <div key={n?.label}>
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="text-white font-semibold text-sm">{n?.label}</span>
                    <span className="text-xs font-mono" style={{ color: 'var(--accent)' }}>{n?.value}</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <div
                      className={`h-full rounded-full nutrient-bar ${animated ? 'animated' : ''}`}
                      style={{
                        width: `${n?.percent}%`,
                        background: n?.color,
                        transitionDelay: `${i * 0.15}s`,
                      }}
                    />
                  </div>
                  <p className="text-white/35 text-xs mt-2">{n?.detail}</p>
                </div>
              ))}
            </div>

            {/* Big stat */}
            <div className="mt-12 pt-10 border-t border-white/10">
              <p className="font-display text-white" style={{ fontSize: 'clamp(3rem, 8vw, 6rem)', fontWeight: 200, lineHeight: 1 }}>
                0<span style={{ color: 'var(--accent)' }}>%</span>
              </p>
              <p className="text-white/50 text-sm mt-2 uppercase tracking-wider">Hidden ingredients. Ever.</p>
            </div>
          </div>

          {/* Right: Label pillars */}
          <div className="reveal reveal-right">
            <p className="text-xs uppercase tracking-widest text-white/40 mb-8">What are we made of?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {labels?.map((item, i) => (
              <div
                key={item?.title}
                className={`p-6 rounded-2xl border transition-all duration-300 hover:border-primary/60 stagger-${i + 1}`}
                style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)' }}
              >
                <span className="mb-4 block">{item?.icon}</span>
                <h3 className="font-semibold text-white text-sm mb-2">{item?.title}</h3>
                <p className="text-white/45 text-xs leading-relaxed">{item?.desc}</p>
              </div>
            ))}

            {/* CTA card spanning full width */}
            <div
              className="sm:col-span-2 p-6 rounded-2xl flex items-center justify-between gap-4"
              style={{ background: 'var(--primary)' }}
            >
              <div>
                <p className="text-primary-foreground font-display text-xl font-[200]">Scan. Read. Trust.</p>
                <p className="text-primary-foreground/70 text-xs mt-1">Your daily nutritional intake at your fingertips.</p>
              </div>
              <a href="/products" aria-label="View nutrition labels on our products" className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/><path d="M14 14h3v3h-3zM17 14h3M17 17v3M14 20h3"/>
                </svg>
              </a>
            </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}